import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

export const AdminFx = () => {
  const [fx, setFx] = useState(null);
  const [ov, setOv] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/fx").then(r => { setFx(r.data); setOv(r.data.overrides || {}); });
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/fx/refresh"); setFx(r.data); toast.success("Rates refreshed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); } finally { setBusy(false); }
  };
  const save = async (extra = {}) => {
    const clean = Object.fromEntries(Object.entries(ov).filter(([, v]) => v !== "" && Number(v) > 0).map(([k, v]) => [k, Number(v)]));
    try { await api.put("/settings", { fx_overrides: clean, fx_auto_update: fx.auto_update, ...extra }); toast.success("Currency settings saved"); load(); }
    catch (e) { toast.error("Save failed"); }
  };
  if (!fx) return null;
  return (
    <div className="space-y-5" data-testid="admin-fx">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold">Display currencies</h3>
          <p className="text-xs text-slate-500">Buyers are always charged in INR. Rates are for display estimates. Last update: {fx.updated_at ? new Date(fx.updated_at).toLocaleString("en-IN") : "never"} ({fx.source || "defaults"})</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm"><Switch checked={fx.auto_update} onCheckedChange={v => { setFx({ ...fx, auto_update: v }); save({ fx_auto_update: v }); }} data-testid="admin-fx-auto" /> Auto daily update</div>
          <Button size="sm" variant="outline" onClick={refresh} disabled={busy} data-testid="admin-fx-refresh"><RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh now</Button>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto bg-white dark:bg-slate-900">
        <table className="w-full text-sm min-w-[520px]">
          <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Currency</th><th className="px-4 py-3">1 INR =</th><th className="px-4 py-3">₹1,000 ≈</th><th className="px-4 py-3">Manual override (blank = use feed)</th></tr></thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {fx.supported.filter(c => c !== "INR").map(c => (
              <tr key={c} data-testid={`admin-fx-row-${c}`}>
                <td className="px-4 py-2 font-bold">{c}</td>
                <td className="px-4 py-2">{fx.rates[c]?.toFixed(6)}</td>
                <td className="px-4 py-2">{(1000 * (fx.rates[c] || 0)).toFixed(2)} {c}</td>
                <td className="px-4 py-2"><Input type="number" step="0.000001" value={ov[c] ?? ""} onChange={e => setOv({ ...ov, [c]: e.target.value })} className="h-8 max-w-[160px]" data-testid={`admin-fx-override-${c}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button onClick={() => save()} className="bg-slate-900 text-white" data-testid="admin-fx-save">Save overrides</Button>
    </div>
  );
};
